import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft,
  ArrowRight,
  Clock3,
  Check,
  ExternalLink,
  Loader2,
  Rocket,
  Sparkles,
} from 'lucide-react';
import { useStripe } from '../contexts/StripeContext';
import AeoPageHead from '../components/AeoPageHead';
import {
  INCUBATOR_CHECKOUT_KEY,
  INCUBATOR_DURATION_LABEL,
  INCUBATOR_FAQS,
  INCUBATOR_F6S_URL,
  INCUBATOR_INCLUDES,
  INCUBATOR_PROCESS,
  INCUBATOR_PRODUCT_CREDITS_LABEL,
  INCUBATOR_PROGRAM_FEE_LABEL,
  INCUBATOR_WHO_FOR,
} from '../lib/incubatorProgram';
import { formatIncubatorDate, getIncubatorWindow } from '../../shared/incubatorSchedule';

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.45 },
};

function formatCountdown(target: Date, now: Date): string {
  const totalSeconds = Math.max(0, Math.floor((target.getTime() - now.getTime()) / 1000));
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  return `${days}d ${hours}h ${minutes}m ${seconds}s`;
}

export default function IncubatorPage() {
  const { openCheckout } = useStripe();
  const [searchParams] = useSearchParams();
  const [accessCode, setAccessCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => new Date());
  const checkoutCancelled = searchParams.get('checkout') === 'cancelled';
  const enrollmentWindow = getIncubatorWindow(now);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  const handlePay = async () => {
    const code = accessCode.trim();
    if (!code) {
      setError('Enter the cohort access code you received after acceptance.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await openCheckout(INCUBATOR_CHECKOUT_KEY, { accessCode: code });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Checkout failed. Please try again.');
      setLoading(false);
    }
  };

  const payControls = (
    <div className="mx-auto w-full max-w-md space-y-3">
      <div
        className={`rounded-xl border px-4 py-3 text-left ${
          enrollmentWindow.isOpen
            ? 'border-emerald-400/25 bg-emerald-400/[0.07]'
            : 'border-amber-300/20 bg-amber-300/[0.06]'
        }`}
        role="status"
      >
        <p className="flex items-center gap-2 text-sm font-semibold">
          <Clock3 className="h-4 w-4" />
          {enrollmentWindow.isOpen ? 'Applications close in' : 'Applications reopen in'}
        </p>
        <p className="mt-1 font-mono text-xl font-semibold tabular-nums">
          {formatCountdown(enrollmentWindow.changesAt, now)}
        </p>
        <p className="mt-1 text-xs text-white/45">
          {formatIncubatorDate(enrollmentWindow.changesAt)}
        </p>
      </div>
      <label className="block text-left text-xs font-medium uppercase tracking-wider text-white/45">
        Cohort access code
        <input
          type="password"
          autoComplete="off"
          value={accessCode}
          onChange={(e) => setAccessCode(e.target.value)}
          placeholder="Provided after acceptance"
          className="mt-2 w-full rounded-xl border border-white/15 bg-black/60 px-4 py-3 text-sm font-normal normal-case tracking-normal text-white placeholder:text-white/30 focus:border-white/30 focus:outline-none disabled:opacity-50"
        />
      </label>
      <div className="flex flex-wrap items-center justify-center gap-3">
        {enrollmentWindow.isOpen ? (
          <a
            href={INCUBATOR_F6S_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-black transition-transform hover:scale-[1.02]"
          >
            Apply on F6S
            <ExternalLink className="h-4 w-4" />
          </a>
        ) : (
          <span className="inline-flex cursor-not-allowed items-center gap-2 rounded-full bg-white/25 px-6 py-3 text-sm font-semibold text-black/60">
            Applications closed
          </span>
        )}
        <button
          type="button"
          onClick={handlePay}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10 disabled:opacity-50"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Starting checkout…
            </>
          ) : (
            <>
              Pay program fee
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>AI Incubator — SELF Labs</title>
        <meta
          name="description"
          content={`SELF Labs AI Incubator — ${INCUBATOR_PROGRAM_FEE_LABEL} program with platform build support, product credits, mentorship, and investment consideration.`}
        />
      </Helmet>
      <AeoPageHead
        path="/incubator"
        faqs={INCUBATOR_FAQS.map((f) => ({ question: f.q, answer: f.a }))}
      />

      <div
        id="incubator"
        data-avatar-target="incubator"
        aria-label="AI Incubator"
        className="min-h-screen bg-black text-white"
      >
        <div
          className="pointer-events-none fixed inset-0 opacity-40"
          style={{
            background:
              'radial-gradient(ellipse 80% 50% at 50% 0%, var(--glow-blue) 0%, transparent 50%), radial-gradient(ellipse 50% 40% at 15% 75%, var(--glow-orange) 0%, transparent 45%), radial-gradient(ellipse 50% 40% at 85% 65%, var(--glow-purple) 0%, transparent 45%)',
          }}
        />

        <div className="relative px-4 pb-20 pt-28 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Link
                to="/products"
                className="inline-flex items-center text-sm text-white/50 transition-colors hover:text-white group"
              >
                <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
                Back to Products
              </Link>
            </motion.div>

            {checkoutCancelled && (
              <p className="mt-6 rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-center text-sm text-white/60">
                Checkout was cancelled. You can pay the program fee below when ready.
              </p>
            )}

            {/* Hero */}
            <section className="mx-auto max-w-3xl pb-14 pt-10 text-center">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-white/80">
                  <Rocket className="h-3.5 w-3.5" />
                  AI Incubator
                </span>
                <h1 className="mt-6 text-4xl font-bold tracking-tight sm:text-5xl">
                  Build with SELF Labs.
                  <span className="block text-white/70">Ship with the stack.</span>
                </h1>
                <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-white/65">
                  A {INCUBATOR_DURATION_LABEL} studio program for AI and platform companies —
                  hands-on build support, product credits, mentorship, and consideration for
                  investment.
                </p>
                <p className="mt-6 font-semibold tracking-tight text-white">
                  Program fee{' '}
                  <span className="text-3xl sm:text-4xl">{INCUBATOR_PROGRAM_FEE_LABEL}</span>
                </p>
                <div className="mt-8">{payControls}</div>
                <p className="mt-4 text-xs text-white/40">
                  New applications follow the countdown above. Accepted companies can pay anytime
                  with their cohort code.
                </p>
                {error && (
                  <p className="mt-4 text-sm text-red-300/90" role="alert">
                    {error}
                  </p>
                )}
              </motion.div>
            </section>

            {/* Included */}
            <section className="border-t border-white/10 py-16">
              <motion.div {...fadeUp} className="mb-10 text-center">
                <h2 className="text-2xl font-bold sm:text-3xl">What&apos;s included</h2>
                <p className="mx-auto mt-3 max-w-xl text-white/55">
                  Fixed program — not a custom build quote. Credits and investment consideration
                  are part of the studio relationship.
                </p>
              </motion.div>
              <div className="grid gap-5 sm:grid-cols-2">
                {INCUBATOR_INCLUDES.map((item, i) => (
                  <motion.div
                    key={item.title}
                    {...fadeUp}
                    transition={{ delay: i * 0.05 }}
                    className="rounded-2xl border border-white/10 bg-white/[0.03] p-6"
                  >
                    <div className="mb-3 flex h-8 w-8 items-center justify-center rounded-full border border-white/15 bg-white/5">
                      <Check className="h-4 w-4 text-white/70" strokeWidth={2} />
                    </div>
                    <h3 className="text-lg font-semibold">{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-white/55">{item.text}</p>
                  </motion.div>
                ))}
              </div>
              <p className="mt-6 text-center text-sm text-white/45">
                Product credits: up to {INCUBATOR_PRODUCT_CREDITS_LABEL} toward SELF Labs
                commercial products.
              </p>
            </section>

            {/* Who + process */}
            <section className="border-t border-white/10 py-16">
              <div className="grid gap-10 lg:grid-cols-2">
                <motion.div {...fadeUp}>
                  <h2 className="text-2xl font-bold">Who it&apos;s for</h2>
                  <ul className="mt-6 space-y-3">
                    {INCUBATOR_WHO_FOR.map((line) => (
                      <li
                        key={line}
                        className="flex items-start gap-3 text-sm leading-relaxed text-white/60"
                      >
                        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-white/40" />
                        {line}
                      </li>
                    ))}
                  </ul>
                </motion.div>
                <motion.div {...fadeUp} transition={{ delay: 0.08 }}>
                  <h2 className="text-2xl font-bold">How it works</h2>
                  <ol className="mt-6 space-y-4">
                    {INCUBATOR_PROCESS.map((item) => (
                      <li
                        key={item.step}
                        className="rounded-xl border border-white/10 bg-white/[0.03] p-4"
                      >
                        <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                          Step {item.step}
                        </p>
                        <h3 className="mt-1 font-semibold">{item.title}</h3>
                        <p className="mt-1 text-sm text-white/55">{item.text}</p>
                      </li>
                    ))}
                  </ol>
                </motion.div>
              </div>
            </section>

            {/* FAQ */}
            <section className="border-t border-white/10 py-16">
              <motion.div {...fadeUp} className="mb-10 text-center">
                <h2 className="text-2xl font-bold">FAQ</h2>
              </motion.div>
              <div className="mx-auto max-w-3xl space-y-4">
                {INCUBATOR_FAQS.map((faq, i) => (
                  <motion.div
                    key={faq.q}
                    {...fadeUp}
                    transition={{ delay: i * 0.04 }}
                    className="rounded-2xl border border-white/10 bg-white/[0.03] p-6"
                  >
                    <h3 className="font-semibold text-white">{faq.q}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-white/55">{faq.a}</p>
                  </motion.div>
                ))}
              </div>
            </section>

            {/* CTA */}
            <section className="border-t border-white/10 py-16">
              <motion.div
                {...fadeUp}
                className="mx-auto max-w-2xl rounded-2xl border border-white/12 bg-white/[0.04] p-8 text-center sm:p-10"
              >
                <h2 className="text-2xl font-bold">Ready to apply?</h2>
                <p className="mt-3 text-white/55">
                  Apply on F6S when the window is open. If you&apos;ve already been accepted, enter
                  your cohort code and pay the {INCUBATOR_PROGRAM_FEE_LABEL} program fee anytime.
                </p>
                <div className="mt-8">{payControls}</div>
                {error && (
                  <p className="mt-4 text-sm text-red-300/90" role="alert">
                    {error}
                  </p>
                )}
                <p className="mt-6 text-sm text-white/40">
                  Questions? Talk to Milan on the homepage or email{' '}
                  <a
                    href="mailto:milan@selflabs.xyz"
                    className="text-white/70 underline-offset-2 hover:underline"
                  >
                    milan@selflabs.xyz
                  </a>
                </p>
              </motion.div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
